import React from 'react';
import { Package, Users, Settings as SettingsIcon } from 'lucide-react';

// Three CTA states per plan, selected by props from UpgradePage:
//   - 'owned'         → user pays for or manages this exact plan today.
//                       CTA: "Manage seats" → onManageSeats(plan).
//   - 'access_via_other' → user already has feature access via *some* sub
//                       (typically an org membership) but doesn't own this
//                       plan. CTA: "Subscribe to invite teammates" with a
//                       one-line explainer. Lets them buy a plan they own.
//   - 'available'     → user has no current access. CTA: "Subscribe" /
//                       "Start Free Trial".
//
// Backwards-compatible: callers passing the old `currentPlanKeys` prop
// still work — those plans are treated as 'owned'.
const PricingCards = ({
  plans,
  interval,
  onSelect,
  onManageSeats,
  ownedPlanKeys = [],
  userHasAnyAccess = false,
  accessProviderName = null,
  currentPlanKeys = [], // legacy alias for ownedPlanKeys
  launchMode = false, // public pricing page during the free launch period
}) => {
  const compartments = plans.filter(p => p.plan_type === 'compartment');
  const bundles = plans.filter(p => p.plan_type === 'bundle');
  const ownedKeys = ownedPlanKeys.length ? ownedPlanKeys : currentPlanKeys;

  const planState = (plan) => {
    if (ownedKeys.includes(plan.key)) return 'owned';
    if (userHasAnyAccess) return 'access_via_other';
    return 'available';
  };

  const ctaLabel = (plan, state) => {
    if (state === 'owned') return 'Manage seats';
    if (state === 'access_via_other') return 'Subscribe to invite teammates';
    return plan.trial_enabled ? 'Start Free Trial' : 'Subscribe';
  };

  const handleCtaClick = (plan, state) => {
    if (state === 'owned') onManageSeats?.(plan);
    else onSelect(plan);
  };

  const getPrice = (plan) => {
    if (interval === 'yearly') {
      const monthly = plan.price_yearly / 12;
      const savedPct = plan.price_monthly > 0
        ? Math.round(((plan.price_monthly * 12 - plan.price_yearly) / (plan.price_monthly * 12)) * 100)
        : 0;
      return { amount: monthly, total: plan.price_yearly, suffix: '/mo', billedLabel: `$${Math.round(plan.price_yearly).toLocaleString()}/yr`, savedPct };
    }
    return { amount: plan.price_monthly, total: plan.price_monthly, suffix: '/mo', billedLabel: null, savedPct: 0 };
  };

  const individualTotal = interval === 'yearly'
    ? compartments.reduce((sum, p) => sum + (p.price_yearly / 12), 0)
    : compartments.reduce((sum, p) => sum + p.price_monthly, 0);

  // Shared card surface: a translucent tint + hairline border (no gradient). Low
  // opacity lets the page background show through, so the card reads as a soft panel
  // rather than a flat block. Light tint in light mode, blueish in dark mode.
  const cardSurface = (owned) => owned
    ? 'border-green-300 dark:border-green-500/40 bg-green-100/20 dark:bg-green-900/30'
    : 'border-blue-200 dark:border-blue-500/30 bg-blue-100/20 dark:bg-blue-900/30';

  return (
    <div>
      {/* Compartment Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {compartments.map(plan => {
          const price = getPrice(plan);
          const state = planState(plan);
          const isCurrent = state === 'owned';
          return (
            <div key={plan.id} className={`relative rounded-2xl border shadow-sm p-6 flex flex-col ${cardSurface(isCurrent)}`}>
              {launchMode && !isCurrent && (
                <span className="absolute -top-3 right-6 bg-green-500 text-white text-xs font-rasa font-medium px-3 py-1 rounded-full">
                  Free during launch
                </span>
              )}
              <div className="flex items-center gap-2 mb-2">
                <Package size={20} className="text-blue-500" />
                <h3 className="text-lg font-dm-serif font-bold text-gray-900 dark:text-white">{plan.name}</h3>
              </div>
              {plan.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 font-rasa mb-4">{plan.description}</p>
              )}
              <div className="mb-4">
                <span className="text-3xl font-dm-serif font-bold text-gray-900 dark:text-white">${Math.round(price.amount)}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-rasa">{price.suffix}</span>
                {price.billedLabel && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 font-rasa mt-1">
                    Billed at {price.billedLabel}
                    {price.savedPct > 0 && (
                      <span className="ml-1.5 text-green-600 dark:text-green-400 font-medium">Save {price.savedPct}%</span>
                    )}
                  </p>
                )}
                {launchMode && (
                  <p className="text-xs text-green-600 dark:text-green-400 font-rasa mt-1">
                    Free to use right now. This price applies when launch pricing goes live.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 font-rasa mb-4">
                <Users size={14} /> {plan.included_seats} seats included
              </div>
              {plan.features && plan.features.length > 0 && (
                <div className="mb-6 flex-1">
                  <p className="font-rasa text-[11px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 mb-1">Included</p>
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700/60 border-t border-gray-100 dark:border-gray-700/60">
                    {plan.features.map(f => (
                      <li key={f.key || f} className="py-2.5 font-rasa text-sm text-gray-700 dark:text-gray-200">
                        {f.name || f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {plan.trial_enabled && state !== 'owned' && (
                <p className="text-xs text-blue-500 font-rasa mb-3">{plan.trial_days}-day free trial available</p>
              )}
              {state === 'access_via_other' && (
                <p className="text-xs text-gray-500 dark:text-gray-400 font-rasa mb-3 leading-relaxed">
                  {accessProviderName
                    ? `You already have access through ${accessProviderName}. Subscribe to get a plan you own with seats to share.`
                    : 'You already have access through another subscription. Subscribe to get a plan you own with seats to share.'}
                </p>
              )}
              <button
                onClick={() => handleCtaClick(plan, state)}
                className={`w-full py-2.5 rounded-lg font-rasa font-medium text-sm transition-colors flex items-center justify-center gap-1.5 ${
                  state === 'owned'
                    ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {state === 'owned' && <SettingsIcon size={14} />}
                {ctaLabel(plan, state)}
              </button>
            </div>
          );
        })}
      </div>

      {/* Bundle Cards */}
      {bundles.map(plan => {
        const price = getPrice(plan);
        const state = planState(plan);
        const isCurrent = state === 'owned';
        const bundleMonthly = interval === 'yearly' ? plan.price_yearly / 12 : plan.price_monthly;
        const savings = individualTotal - bundleMonthly;
        const savingsPct = individualTotal > 0 ? Math.round((savings / individualTotal) * 100) : 0;

        return (
          <div key={plan.id} className={`rounded-2xl border shadow-sm p-7 ${cardSurface(isCurrent)}`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`h-1.5 w-1.5 rounded-full ${isCurrent ? 'bg-green-500' : 'bg-blue-500'}`} />
                  <span className={`font-rasa text-[11px] uppercase tracking-[0.2em] ${isCurrent ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}>
                    {isCurrent ? 'Your plan' : savingsPct > 0 ? `Best value · Save ${savingsPct}%` : 'Best value'}
                  </span>
                </div>
                <h3 className="text-2xl font-dm-serif font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-rasa mt-1">{plan.description}</p>
                <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 font-rasa mt-1">
                  <Users size={14} /> {plan.included_seats} seats included
                </div>
                {plan.compartments && (
                  <div className="mt-4">
                    <p className="font-rasa text-[11px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 mb-1">Included compartments</p>
                    <ul className="divide-y divide-gray-100 dark:divide-gray-700/60 border-t border-gray-100 dark:border-gray-700/60">
                      {plan.compartments.map(c => (
                        <li key={c.key || c} className="py-2.5 font-rasa text-sm text-gray-700 dark:text-gray-200">
                          {c.name || (typeof c === 'string' ? c.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()) : c)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="mb-2">
                  <span className="text-4xl font-dm-serif font-bold text-gray-900 dark:text-white">${Math.round(price.amount)}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 font-rasa">{price.suffix}</span>
                  {price.billedLabel && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 font-rasa mt-1">
                      Billed at {price.billedLabel}
                    </p>
                  )}
                  {launchMode && (
                    <p className="text-xs text-green-600 dark:text-green-400 font-rasa mt-1">
                      Free during launch
                    </p>
                  )}
                </div>
                {savingsPct > 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 font-rasa line-through mb-2">
                    ${Math.round(individualTotal)}/mo individually
                  </p>
                )}
                {state === 'access_via_other' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-rasa mb-2 leading-relaxed max-w-[220px] ml-auto">
                    {accessProviderName
                      ? `Access via ${accessProviderName} — subscribe for your own seats.`
                      : 'You already have access — subscribe for seats to share.'}
                  </p>
                )}
                <button
                  onClick={() => handleCtaClick(plan, state)}
                  className={`px-8 py-3 rounded-lg font-rasa font-medium text-sm transition-colors inline-flex items-center justify-center gap-1.5 ${
                    state === 'owned'
                      ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {state === 'owned' && <SettingsIcon size={14} />}
                  {state === 'owned' ? 'Manage seats' : state === 'access_via_other' ? 'Subscribe to invite teammates' : 'Get All Access'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PricingCards;
